import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, 
  MapPin, 
  Clock, 
  User, 
  Globe, 
  Monitor, 
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Activity,
  Users,
  Map,
  Calendar,
  Timer,
  PlusCircle,
  Pencil,
  Trash2,
  Ban,
} from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

export default function LoginLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('login');
  const logsPerPage = 20;

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [currentPage]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/login-logs?limit=${logsPerPage}&skip=${currentPage * logsPerPage}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to load login logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/login-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeAgo = (isoString) => {
    if (!isoString) return '';
    const now = new Date();
    const then = new Date(isoString);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getRoleBadge = (role) => {
    const colors = {
      'admin': 'bg-red-100 text-red-800',
      'manager': 'bg-blue-100 text-blue-800',
      'user': 'bg-slate-100 text-slate-800'
    };
    return (
      <Badge className={colors[role] || colors.user}>
        {role?.charAt(0).toUpperCase() + role?.slice(1) || 'User'}
      </Badge>
    );
  };

  const filteredLogs = logs.filter(log => 
    log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.ip_address?.includes(searchTerm) ||
    log.geo_location?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.geo_location?.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(total / logsPerPage);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center justify-center gap-3">
            <Shield className="w-8 h-8 text-red-600" />
            Login Activity Log
          </h1>
          <p className="text-slate-600 mt-1">Monitor user login activity and geographic locations</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('login')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${activeTab === 'login' ? 'border-red-600 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            data-testid="tab-login-activity"
          >
            Login Activity
          </button>
          <button
            onClick={() => setActiveTab('booking')}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${activeTab === 'booking' ? 'border-red-600 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            data-testid="tab-booking-activity"
          >
            Booking Activity
          </button>
        </div>

        {activeTab === 'booking' && <BookingActivity />}

        {activeTab === 'login' && (<>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500 text-white">
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-blue-700">Total Logins</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total_logins}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500 text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-green-700">Unique Users</p>
                  <p className="text-2xl font-bold text-green-900">{stats.unique_users}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500 text-white">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-purple-700">Last 24 Hours</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.recent_logins_24h}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500 text-white">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-orange-700">Top Country</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {stats.logins_by_country?.[0]?._id || 'N/A'}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Search and Refresh */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, IP, or location..."
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </Card>

        {/* Login Logs Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Role</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Login Time</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">IP Address</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Location</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-slate-500">
                        No login logs found
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log, index) => (
                      <tr key={log.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{log.user_name}</p>
                              <p className="text-sm text-slate-500">{log.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {getRoleBadge(log.user_role)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="text-sm text-slate-900">{formatDateTime(log.login_time)}</p>
                              <p className="text-xs text-slate-500">{getTimeAgo(log.login_time)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Monitor className="w-4 h-4 text-slate-400" />
                            <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                              {log.ip_address || 'Unknown'}
                            </code>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {log.geo_location?.city ? (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-red-500" />
                              <div>
                                <p className="text-sm text-slate-900">
                                  {log.geo_location.city}, {log.geo_location.region}
                                </p>
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Globe className="w-3 h-3" />
                                  {log.geo_location.country}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">Location unavailable</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-xs text-slate-500 max-w-[200px] truncate" title={log.user_agent}>
                            {log.user_agent?.includes('Mobile') ? '📱 Mobile' : '💻 Desktop'}
                          </p>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > logsPerPage && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
              <p className="text-sm text-slate-600">
                Showing {currentPage * logsPerPage + 1} - {Math.min((currentPage + 1) * logsPerPage, total)} of {total} logs
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Country Distribution */}
        {stats?.logins_by_country && stats.logins_by_country.length > 0 && (
          <Card className="p-6">
            <h3 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Map className="w-5 h-5" />
              Logins by Country
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {stats.logins_by_country.map((country, index) => (
                <div key={country._id || index} className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="font-semibold text-slate-900">{country._id || 'Unknown'}</p>
                  <p className="text-2xl font-bold text-primary">{country.count}</p>
                  <p className="text-xs text-slate-500">logins</p>
                </div>
              ))}
            </div>
          </Card>
        )}
        </>)}
      </div>
    </DashboardLayout>
  );
}


const BA_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'mtd', label: 'Month to Date' },
  { key: 'custom', label: 'Custom Range' },
];

const fmtActive = (mins) => {
  if (!mins || mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};

function BookingActivity() {
  const [range, setRange] = useState('today');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${API}/login-logs/booking-activity?range=${range}`;
      if (range === 'custom' && start && end) url += `&start=${start}&end=${end}`;
      const r = await axios.get(url);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load booking activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (range !== 'custom' || (start && end)) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, start, end]);

  const rows = data?.rows || [];
  const t = data?.totals || { created: 0, edited: 0, deleted: 0, cancelled: 0, active_minutes: 0 };

  const cols = [
    { key: 'created', label: 'Created', icon: PlusCircle, cls: 'text-emerald-600' },
    { key: 'edited', label: 'Edited', icon: Pencil, cls: 'text-blue-600' },
    { key: 'cancelled', label: 'Cancelled', icon: Ban, cls: 'text-amber-600' },
    { key: 'deleted', label: 'Deleted', icon: Trash2, cls: 'text-red-600' },
  ];

  return (
    <div className="space-y-4" data-testid="booking-activity-tab">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {BA_RANGES.map((r) => (
          <Button
            key={r.key}
            variant={range === r.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRange(r.key)}
            className={range === r.key ? 'bg-red-600 hover:bg-red-700' : ''}
            data-testid={`ba-range-${r.key}`}
          >
            {r.label}
          </Button>
        ))}
        {range === 'custom' && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="pl-8 w-40" data-testid="ba-start" />
            </div>
            <span className="text-slate-400 text-sm">to</span>
            <div className="relative">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} className="pl-8 w-40" data-testid="ba-end" />
            </div>
          </div>
        )}
      </div>

      {/* Summary totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cols.map((c) => (
          <Card key={c.key} className="p-4 border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
              <c.icon className={`w-4 h-4 ${c.cls}`} /> {c.label}
            </div>
            <p className={`text-2xl font-bold mt-1 ${c.cls}`} data-testid={`ba-total-${c.key}`}>{t[c.key]}</p>
          </Card>
        ))}
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
            <Timer className="w-4 h-4 text-violet-600" /> Active Time
          </div>
          <p className="text-2xl font-bold mt-1 text-violet-600" data-testid="ba-total-active">{fmtActive(t.active_minutes)}</p>
        </Card>
      </div>

      {/* Per-user table */}
      <Card className="border-slate-200">
        <table className="w-full text-sm" data-testid="booking-activity-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-4 font-semibold text-slate-700">User</th>
              <th className="text-center p-4 font-semibold text-slate-700">Created</th>
              <th className="text-center p-4 font-semibold text-slate-700">Edited</th>
              <th className="text-center p-4 font-semibold text-slate-700">Cancelled</th>
              <th className="text-center p-4 font-semibold text-slate-700">Deleted</th>
              <th className="text-right p-4 font-semibold text-slate-700">Active Time</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">No activity in this period</td></tr>
            ) : rows.map((r) => (
              <tr key={r.user} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ba-row-${r.user}`}>
                <td className="p-4 font-medium text-slate-900">{r.user}</td>
                <td className="p-4 text-center text-emerald-600 font-semibold">{r.created}</td>
                <td className="p-4 text-center text-blue-600 font-semibold">{r.edited}</td>
                <td className="p-4 text-center text-amber-600 font-semibold">{r.cancelled}</td>
                <td className="p-4 text-center text-red-600 font-semibold">{r.deleted}</td>
                <td className="p-4 text-right text-violet-600 font-semibold">{fmtActive(r.active_minutes)}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-900 text-white">
              <tr data-testid="ba-totals-row">
                <td className="p-4 font-bold">Totals</td>
                <td className="p-4 text-center font-bold">{t.created}</td>
                <td className="p-4 text-center font-bold">{t.edited}</td>
                <td className="p-4 text-center font-bold">{t.cancelled}</td>
                <td className="p-4 text-center font-bold">{t.deleted}</td>
                <td className="p-4 text-right font-bold">{fmtActive(t.active_minutes)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>
      <p className="text-xs text-slate-400">
        Active Time is estimated from login &amp; action timestamps grouped into sessions (30-min idle gap). Deleted counts are tracked from the day this feature was enabled.
      </p>
    </div>
  );
}
